import React, { useEffect, useState } from 'react';
import { Button, Card, Carousel, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IInfluencerReport, IReportStats } from '../../../../../../model/measure/IInfluencerReport';
import { IReportItem } from '../../../../../../model/measure/IReportItem';
import { IPicture } from '../../../../../../model/shared/IPicture';
import { IPostImages } from '../../../../../../model/shared/IPostImages';
import { CustomImageHandler } from '../../../../../../shared/components/CustomImageHandler';
import PlayButtonOverlay from '../../../../../../shared/components/button/PlayButtonOverlay';
import {
    formatDate,
    getCorrectImageThumbnail,
    hasAccessRights,
    truncateString,
} from '../../../../../../shared/functions/Functions';
import { numberFormatter } from '../../../../../../utils/helpers/numberFormater';
import { DetailedPerInfluencerModal } from './DetailedPerInfluencerModal';
import { LeaveReviewModal } from './LeaveReviewModal';
import { priceSplitter } from '../../../../../../utils/helpers/priceSplitter';
import { ErrorToast, WarningToast } from '../../../../../../utils/toasters';
import { IBundle } from '../../../../../../model/options/IBundle';
import { UserService } from '../../../../../shared/user/service';
import CommentModal from '../../../../../../shared/components/modal/CommentModal';
import { AccessRightEnum } from '../../../../../../enum/AccessRightEnum';
import { InternalRoutes } from '../../../../../../enum/InternalRoutes';
import { useNavigate } from 'react-router-dom';
import { getFileType } from '../../../../../../shared/components/placeholder/FilePlaceholder';
import TooltipOverlay from '../../../../../../shared/components/tooltip/Tooltip';

interface IInfluencerProps {
    influencer: IInfluencerReport;
    setUpdateReview: any;
    updateReview: boolean;
}
export const reportingFields = [
    'impressions',
    'reach',
    'engagementRate',
    'totalLinkClicks',
    'clickTrough',
    'reachCpm',
    'totalExecutedPrice',
];

export const DetailedPerInfluencer = ({ influencer, updateReview, setUpdateReview }: IInfluencerProps) => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [showReview, setShowReview] = useState(false);
    const [selectedFeedItemImage, setSelectedFeedItemImage] = useState<IPicture | IPostImages>();
    const [offerId, setOfferId] = useState(0);
    const [selectedImageArray, setSelectedImageArray] = useState<IPicture[]>([]);
    const [reportItemId, setReportItemId] = useState(0);
    const [bundle, setBundle] = useState<IBundle | undefined>(undefined);
    const [commentModal, setCommentModal] = useState({
        show: false,
    });
    const [triggerComment, setTriggerComment] = useState(false);
    const navigate = useNavigate();
    const isLongTerm = influencer?.isLongTerm;
    const isAddonsOnly = influencer?.isAddonsOnly;

    const openModal = (id: number, image: IPicture | IPostImages | null, arrayToLoop?: IPicture[]) => {
        setShowModal(true);
        arrayToLoop && setSelectedImageArray(arrayToLoop.length ? arrayToLoop : []);
        image && setSelectedFeedItemImage(image);
        setReportItemId(id);
    };

    const goToProfileInfo = () => {
        navigate(`${InternalRoutes.INFLUENCER_PUBLIC_PROFILE}/${influencer?.user?.id}`);
    };

    const closeModal = () => {
        setShowReview(false);
        setUpdateReview(!updateReview);
    };

    useEffect(() => {
        if (!!offerId) {
            UserService.getPrivateComment(offerId)
                .then(response => {
                    setBundle({ ...response, feedback: !!bundle?.feedback ? bundle.feedback : response.feedback });
                })
                .catch(err => ErrorToast(err));
        }
    }, [offerId, triggerComment]);

    const [newMessage, setNewMessage] = useState<string>('');
    const [isSendDisabled, setIsSendDisabled] = useState(false);

    const sendMessageHandler = async () => {
        if (!newMessage?.length) {
            return WarningToast(t('general.toasts.warnEmptyMessage'));
        }
        setIsSendDisabled(true);
        await UserService.putPrivateComment(offerId, { message: newMessage.replace(/\n/g, '</br>') })
            .then(() => {
                setNewMessage('');
                setTriggerComment((prev: any) => !prev);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsSendDisabled(false));
    };
    const isReportItemSubmitted = influencer?.reportedPlatforms.some(platform => !!platform.reportItem.length);
    const name = influencer.user.displayName;
    const truncatedName = truncateString(name, 25);
    const isTruncated = name.length > 25;
    return (
        <>
            <DetailedPerInfluencerModal
                show={showModal}
                selectedImageArray={selectedImageArray}
                selectedFeedItemImage={selectedFeedItemImage}
                closeModal={() => setShowModal(false)}
                reportItemId={reportItemId}
            />
            <LeaveReviewModal show={showReview} offerId={offerId} closeModal={closeModal} />
            <CommentModal
                {...commentModal}
                closeModal={setCommentModal}
                sendMessageHandler={sendMessageHandler}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                isSendDisabled={isSendDisabled}
                bundle={bundle}
                canMention={false}
                title={t('brand.campaign.dashboard.measure.leave_comment_title')}
                textNoComments={t('brand.campaign.dashboard.measure.leave_comment_desc')}
                hideOptionsCommentsTitle={true}
                showCloseBtn={true}
                hideFooter={true}
            />
            <Card className="mb-3">
                <Card.Body>
                    <Row className="d-flex align-items-center justify-content-between ">
                        <Col className="d-md-flex align-items-center justify-content-between">
                            <div onClick={goToProfileInfo} className="d-flex align-items-center cursor-pointer">
                                <div className="position-relative">
                                    <CustomImageHandler
                                        classes="campaign-icon rounded-circle"
                                        altTag="User Profile"
                                        photoPath={influencer?.user?.profilePhoto?.thumbnail as string}
                                        thumbnailPath={influencer?.user?.profilePhoto?.thumbnail}
                                        name={
                                            influencer?.user?.displayName
                                                ? influencer?.user?.displayName
                                                : `${influencer?.user?.firstName ?? ''}${
                                                      influencer?.user?.lastName ? ' ' + influencer?.user?.lastName : ''
                                                  }`.trim()
                                        }
                                    />
                                </div>

                                <div className="px-lg-2 ml-2">
                                    <div className="d-flex flex-md-row flex-wrap align-items-start">
                                        <h5 className="mb-0 mr-2">
                                            {truncateString(
                                                influencer?.user?.displayName
                                                    ? influencer?.user?.displayName
                                                    : `${influencer?.user?.firstName ?? ''}${
                                                          influencer?.user?.lastName
                                                              ? ' ' + influencer?.user?.lastName
                                                              : ''
                                                      }`.trim(),
                                                40
                                            )}

                                            <br />

                                            <TooltipOverlay content={isTruncated ? name : ''}>
                                                <span className="text-muted">
                                                    {truncateString(
                                                        influencer?.user?.displayName
                                                            ? `${influencer?.user?.firstName ?? ''}${
                                                                  influencer?.user?.lastName
                                                                      ? ' ' + influencer?.user?.lastName
                                                                      : ''
                                                              }`.trim() || '-'
                                                            : '-',
                                                        40
                                                    )}
                                                </span>
                                            </TooltipOverlay>
                                        </h5>

                                        {isLongTerm && (
                                            <div className={'custom-badge long-term mx-1'}>
                                                {t(`brand.campaign.briefing.overview_card.long_term`)}
                                            </div>
                                        )}

                                        {isAddonsOnly && (
                                            <div className={'custom-badge additional ml-1'}>
                                                {t('influencer.offer_management.modal.additional')}
                                            </div>
                                        )}

                                        <span className="text-muted">Offer ID: #{influencer?.id}</span>
                                    </div>
                                </div>
                            </div>
                            {isReportItemSubmitted && (
                                <div className="d-flex align-items-center mt-3 mt-md-0">
                                    <Button
                                        className="btn-outline-primary mr-2 w-100"
                                        onClick={() => {
                                            setCommentModal(prev => ({ ...prev, show: true }));
                                            setOfferId(influencer?.id);
                                        }}
                                    >
                                        {t('general.button.comment')}
                                    </Button>
                                    {influencer.isReviewable ? (
                                        <button
                                            onClick={() => {
                                                setShowReview(true);
                                                setOfferId(influencer?.id);
                                            }}
                                            className="btn btn-outline-primary col-auto"
                                        >
                                            {t('general.button.leaveReview')}
                                        </button>
                                    ) : (
                                        <span className="font-weight-semi-bold text-primary">
                                            {t('brand.campaign.measure.reviewSubmitted')}
                                        </span>
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                    <div className="h-100 align-items-center mt-3 m-0 ">
                        <div
                            className="my-3 d-flex flex-wrap justify-content-lg-around justify-content-evenly"
                            style={{ rowGap: '8px' }}
                        >
                            <div className="px-2">
                                <h5 className="text-primary ">
                                    {numberFormatter(influencer?.reportStats?.reportCount)} /{' '}
                                    {influencer?.reportStats?.nbPosts}
                                </h5>
                                <p className="mb-0">{t(`influencer.reporting.reported`)}</p>
                            </div>
                            {reportingFields?.map(item => (
                                <div className="px-2" key={item}>
                                    {hasAccessRights(AccessRightEnum.MEASURE_HIDE_COSTS) &&
                                    ['reachCpm', 'totalExecutedPrice'].includes(item) ? null : (
                                        <>
                                            <h5
                                                className={`text-primary ${
                                                    ['reachCpm', 'totalExecutedPrice'].includes(item)
                                                        ? ' euro-symbol'
                                                        : ''
                                                }`}
                                            >
                                                {priceSplitter(
                                                    (influencer?.reportStats?.[
                                                        item as unknown as keyof IReportStats
                                                    ] as number) ?? 0
                                                )}
                                                {['engagementRate', 'clickTrough'].includes(item) ? ' %' : ''}
                                            </h5>
                                            <p className="mb-0">{t(`influencer.reporting.${item}`)}</p>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="position-relative d-flex flex-wrap justify-content-center justify-content-md-start">
                            {influencer?.reportedPlatforms?.map(platforms => {
                                return platforms?.reportItem?.map(reportItem => {
                                    const arrayToLoop =
                                        getCorrectImages(
                                            reportItem,
                                            !!reportItem?.relatedFeedItem?.postImages?.length
                                        ) || [];
                                    return (
                                        <React.Fragment key={reportItem?.id}>
                                            {!!arrayToLoop?.length ? (
                                                arrayToLoop?.map((image, currentIndex) => {
                                                    return (
                                                        <div
                                                            key={image?.id}
                                                            className={`reporting-card-brand border-d  px-0 ${
                                                                currentIndex > 0 ? 'd-none' : ''
                                                            }`}
                                                        >
                                                            <Carousel
                                                                indicators={arrayToLoop?.length > 1}
                                                                className="dashboard-slider"
                                                                controls={arrayToLoop?.length > 1}
                                                            >
                                                                {!!arrayToLoop?.length ? (
                                                                    arrayToLoop?.map(image => {
                                                                        return (
                                                                            <Carousel.Item
                                                                                interval={5000}
                                                                                key={image?.id}
                                                                                className={'cursor-pointer'}
                                                                                onClick={() =>
                                                                                    openModal(
                                                                                        reportItem.id,
                                                                                        image,
                                                                                        arrayToLoop
                                                                                    )
                                                                                }
                                                                            >
                                                                                {image.externalLink ? (
                                                                                    <CustomImageHandler
                                                                                        photoPath={getCorrectImageThumbnail(
                                                                                            !!image?.externalLink,
                                                                                            image?.originalName ?? '',
                                                                                            image?.thumbPath ?? '',
                                                                                            image?.path
                                                                                        )}
                                                                                        altTag="Event"
                                                                                        thumbnailPath={image?.thumbnail}
                                                                                        placeholder={getCorrectImageThumbnail(
                                                                                            !!image?.externalLink,
                                                                                            image?.originalName ?? '',
                                                                                            image?.thumbPath ?? '',
                                                                                            image?.path
                                                                                        )}
                                                                                        classes="report-item-img"
                                                                                    />
                                                                                ) : image?.type?.includes('video') ? (
                                                                                    <PlayButtonOverlay>
                                                                                        <CustomImageHandler
                                                                                            altTag={image?.name}
                                                                                            classes="report-item-img"
                                                                                            thumbnailPath={
                                                                                                image?.thumbnail ||
                                                                                                image?.path
                                                                                            }
                                                                                            photoPath={getCorrectImageThumbnail(
                                                                                                !!image?.externalLink,
                                                                                                image?.originalName ??
                                                                                                    '',
                                                                                                image?.thumbPath ?? '',
                                                                                                image?.path
                                                                                            )}
                                                                                        />
                                                                                    </PlayButtonOverlay>
                                                                                ) : (
                                                                                    <CustomImageHandler
                                                                                        altTag={image?.name}
                                                                                        classes={`
                                                                                            report-item-img ${
                                                                                                reportItem?.event
                                                                                                    ?.isRejected &&
                                                                                                'danger-border'
                                                                                            }
                                                                                        `}
                                                                                        thumbnailPath={
                                                                                            getFileType(
                                                                                                image.name,
                                                                                                image?.thumbnail ||
                                                                                                    image?.path
                                                                                            ).imgSrc
                                                                                        }
                                                                                        photoPath={
                                                                                            getFileType(
                                                                                                image.name,
                                                                                                getCorrectImageThumbnail(
                                                                                                    !!image?.externalLink,
                                                                                                    image?.originalName ??
                                                                                                        '',
                                                                                                    image?.thumbPath ??
                                                                                                        '',
                                                                                                    image?.path
                                                                                                )
                                                                                            ).imgSrc
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            </Carousel.Item>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <Carousel.Item>
                                                                        <CustomImageHandler
                                                                            photoPath={'/'}
                                                                            onClick={() =>
                                                                                openModal(
                                                                                    reportItem.id,
                                                                                    null,
                                                                                    arrayToLoop
                                                                                )
                                                                            }
                                                                            classes="report-item-img"
                                                                            altTag="Instagram post example"
                                                                        />
                                                                    </Carousel.Item>
                                                                )}
                                                            </Carousel>
                                                            <TooltipOverlay
                                                                className="top-right-reporting-icon"
                                                                content={
                                                                    reportItem?.event?.isVerified
                                                                        ? t('brand.campaign.measure.verified')
                                                                        : reportItem?.event?.isRejected
                                                                        ? t('brand.campaign.measure.rejected')
                                                                        : t('brand.campaign.measure.being_verified')
                                                                }
                                                            >
                                                                {reportItem?.event?.isVerified ? (
                                                                    <img
                                                                        src="/icons/approved-icon.svg"
                                                                        alt="Approved icon"
                                                                    />
                                                                ) : reportItem?.event?.isRejected ? (
                                                                    <img
                                                                        src="/icons/not-approved-icon.svg"
                                                                        alt="Not approved icon"
                                                                    />
                                                                ) : (
                                                                    <img
                                                                        src="/icons/being-verified-icon.svg"
                                                                        alt="Being verified icon"
                                                                    />
                                                                )}
                                                            </TooltipOverlay>
                                                            <div className="d-flex align-items-center my-2">
                                                                <img
                                                                    src={platforms?.socialPlatform?.path}
                                                                    alt={platforms?.socialPlatform?.name}
                                                                />
                                                                <div className="d-flex flex-column">
                                                                    <span className="font-weight-semi-bold ml-1">
                                                                        {t(
                                                                            `general.platforms.${platforms?.socialPlatform?.name}`
                                                                        )}{' '}
                                                                        {reportItem?.event?.platformPosition ?? '-'}/
                                                                        {platforms?.nbOfferedPosts}
                                                                    </span>
                                                                    {reportItem?.createdAt && (
                                                                        <span className="text-muted ml-1">
                                                                            {formatDate(reportItem.createdAt)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="reporting-card-brand">
                                                    <CustomImageHandler
                                                        photoPath={'/'}
                                                        onClick={() => openModal(reportItem.id, null, arrayToLoop)}
                                                        classes="report-item-img"
                                                        altTag="Instagram post example"
                                                    />
                                                    <TooltipOverlay
                                                        className="top-right-reporting-icon"
                                                        content={
                                                            reportItem?.event?.isVerified
                                                                ? t('brand.campaign.measure.verified')
                                                                : t('brand.campaign.measure.being_verified')
                                                        }
                                                    >
                                                        {reportItem?.event?.isVerified ? (
                                                            <img src="/icons/approved-icon.svg" alt="Approved icon" />
                                                        ) : (
                                                            <img
                                                                src="/icons/being-verified-icon.svg"
                                                                alt="Being verified icon"
                                                            />
                                                        )}
                                                    </TooltipOverlay>
                                                    <div className="d-flex align-items-center my-2">
                                                        <img
                                                            src={platforms?.socialPlatform?.path}
                                                            alt={platforms?.socialPlatform?.name}
                                                        />
                                                        <div className="d-flex flex-column">
                                                            <span className="font-weight-semi-bold ml-1">
                                                                {t(
                                                                    `general.platforms.${platforms?.socialPlatform?.name}`
                                                                )}{' '}
                                                                {reportItem?.event?.platformPosition ?? '-'}/
                                                                {platforms?.nbOfferedPosts}
                                                            </span>
                                                            {reportItem?.createdAt && (
                                                                <span className="text-muted ml-1">
                                                                    {formatDate(reportItem.createdAt)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                });
                            })}
                        </div>
                        <div className="position-relative d-flex flex-wrap justify-content-center justify-content-md-start">
                            {influencer?.additionalReportedItems?.map((reportItem, index: number) => {
                                const arrayToLoop =
                                    getCorrectImages(reportItem, !!reportItem?.relatedFeedItem?.postImages?.length) ||
                                    [];
                                return (
                                    <React.Fragment key={index}>
                                        {!!arrayToLoop?.length ? (
                                            arrayToLoop?.map((image, currentIndex) => {
                                                return (
                                                    <div
                                                        key={image?.id}
                                                        className={`reporting-card-brand ${
                                                            currentIndex > 0 ? 'd-none' : ''
                                                        }`}
                                                    >
                                                        <Carousel
                                                            indicators={arrayToLoop?.length > 1}
                                                            className="dashboard-slider"
                                                            controls={arrayToLoop?.length > 1}
                                                        >
                                                            {!!arrayToLoop?.length ? (
                                                                arrayToLoop?.map(image => {
                                                                    return (
                                                                        <Carousel.Item
                                                                            interval={5000}
                                                                            key={image?.id}
                                                                            className={'cursor-pointer'}
                                                                            onClick={() =>
                                                                                openModal(
                                                                                    reportItem.id,
                                                                                    image,
                                                                                    arrayToLoop
                                                                                )
                                                                            }
                                                                        >
                                                                            {image?.type?.includes('video') ? (
                                                                                <PlayButtonOverlay>
                                                                                    <CustomImageHandler
                                                                                        altTag={image?.name}
                                                                                        classes="report-item-img"
                                                                                        thumbnailPath={
                                                                                            image?.thumbnail ||
                                                                                            image?.path
                                                                                        }
                                                                                        photoPath={getCorrectImageThumbnail(
                                                                                            !!image?.externalLink,
                                                                                            image?.originalName ?? '',
                                                                                            image?.thumbPath ?? '',
                                                                                            image?.path
                                                                                        )}
                                                                                    />
                                                                                </PlayButtonOverlay>
                                                                            ) : (
                                                                                <CustomImageHandler
                                                                                    altTag={image?.name}
                                                                                    classes="report-item-img"
                                                                                    thumbnailPath={
                                                                                        image?.thumbnail || image?.path
                                                                                    }
                                                                                    photoPath={getCorrectImageThumbnail(
                                                                                        !!image?.externalLink,
                                                                                        image?.originalName ?? '',
                                                                                        image?.thumbPath ?? '',
                                                                                        image?.path
                                                                                    )}
                                                                                />
                                                                            )}
                                                                        </Carousel.Item>
                                                                    );
                                                                })
                                                            ) : (
                                                                <Carousel.Item>
                                                                    <CustomImageHandler
                                                                        photoPath={'/'}
                                                                        onClick={() =>
                                                                            openModal(reportItem.id, null, arrayToLoop)
                                                                        }
                                                                        classes="report-item-img"
                                                                        altTag="Instagram post example"
                                                                    />
                                                                </Carousel.Item>
                                                            )}
                                                        </Carousel>
                                                        <TooltipOverlay
                                                            className="top-right-reporting-icon"
                                                            content={
                                                                reportItem?.isVerified
                                                                    ? t('brand.campaign.measure.verified')
                                                                    : t('brand.campaign.measure.being_verified')
                                                            }
                                                        >
                                                            {reportItem?.isVerified ? (
                                                                <img
                                                                    src="/icons/approved-icon.svg"
                                                                    alt="Approved icon"
                                                                />
                                                            ) : (
                                                                <img
                                                                    src="/icons/being-verified-icon.svg"
                                                                    alt="Being verified icon"
                                                                />
                                                            )}
                                                        </TooltipOverlay>
                                                        <div className="d-flex align-items-center justify-content-start my-2">
                                                            <img
                                                                src={reportItem?.socialPlatform?.path}
                                                                alt={reportItem?.socialPlatform?.name}
                                                            />
                                                            <div className="d-flex flex-column">
                                                                <span className="font-weight-semi-bold ml-1">
                                                                    Additional{' '}
                                                                    {t(
                                                                        `general.platforms.${reportItem?.socialPlatform?.name}`
                                                                    )}{' '}
                                                                    {index + 1}
                                                                </span>
                                                                {reportItem?.createdAt && (
                                                                    <span className="text-muted ml-1">
                                                                        {formatDate(reportItem.createdAt)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="reporting-card-brand">
                                                <CustomImageHandler
                                                    photoPath={'/'}
                                                    onClick={() => openModal(reportItem.id, null, arrayToLoop)}
                                                    classes="report-item-img"
                                                    altTag="Instagram post example"
                                                />
                                                <TooltipOverlay
                                                    className="top-right-reporting-icon"
                                                    content={
                                                        reportItem?.isVerified
                                                            ? t('brand.campaign.measure.verified')
                                                            : t('brand.campaign.measure.being_verified')
                                                    }
                                                >
                                                    {reportItem?.isVerified ? (
                                                        <img src="/icons/approved-icon.svg" alt="Approved icon" />
                                                    ) : (
                                                        <img
                                                            src="/icons/being-verified-icon.svg"
                                                            alt="Being verified icon"
                                                        />
                                                    )}
                                                </TooltipOverlay>
                                                <div className="d-flex align-items-center justify-content-start my-2 ${currentIndex > 0 ? 'd-none' : ''}">
                                                    <img
                                                        src={reportItem?.socialPlatform?.path}
                                                        alt={reportItem?.socialPlatform?.name}
                                                    />
                                                    <div className="d-flex flex-column">
                                                        <span className="font-weight-semi-bold ml-1">
                                                            Additional{' '}
                                                            {t(`general.platforms.${reportItem?.socialPlatform?.name}`)}{' '}
                                                            {index + 1}
                                                        </span>
                                                        {reportItem?.createdAt && (
                                                            <span className="text-muted ml-1">
                                                                {formatDate(reportItem.createdAt)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </Card.Body>
                {/*<Card.Footer className="d-flex justify-content-between align-items-center">*/}
                {/*    <span className="text-muted ">Offer ID: #{influencer?.id}</span>*/}
                {/*    {isReportItemSubmitted && (*/}
                {/*        <div className="d-flex align-items-center min-width-max-content">*/}
                {/*            <Button className="btn-outline-primary mr-2 w-100" onClick={() => {*/}
                {/*                setCommentModal((prev) => ({...prev, show: true}));*/}
                {/*                setOfferId(influencer?.id);*/}
                {/*            }}>*/}
                {/*                {t('general.button.comment')}*/}
                {/*            </Button>*/}
                {/*            {influencer.isReviewable ? (*/}
                {/*                <button onClick={() => {*/}
                {/*                    setShowReview(true);*/}
                {/*                    setOfferId(influencer?.id);*/}
                {/*                }}*/}
                {/*                        className="btn btn-outline-primary col-auto">*/}
                {/*                    {t('general.button.leaveReview')}*/}
                {/*                </button>*/}
                {/*            ) : (*/}
                {/*                <span className="font-weight-semi-bold text-primary">*/}
                {/*                    {t('brand.campaign.measure.reviewSubmitted')}*/}
                {/*                </span>*/}
                {/*            )}*/}
                {/*        </div>*/}
                {/*    )}*/}
                {/*</Card.Footer>*/}
            </Card>
        </>
    );
};

const getCorrectImages = (arr: IReportItem, hasPostImages: boolean) => {
    return hasPostImages ? arr?.relatedFeedItem?.postImages : arr?.attachments;
};
